/* Google sign-in — REDIRECT flow, not the PocketBase SDK popup.

   PB's all-in-one authWithOAuth2 popup needs a realtime/SSE handshake to receive the auth
   code, and the Cloudflare tunnel in front of PocketBase blocks realtime — so the popup opens
   about:blank and hangs forever. Every CORE app uses this redirect flow instead: build the
   Google URL from listAuthMethods, stash the PKCE verifier + state, full-page redirect, then
   exchange the code on the callback route.

   Two differences from the CORE copies, both deliberate:

   1. The callback carries the /real-or-fake base path. The other apps live on subdomains, so
      theirs is `${origin}/auth/google/callback`. This app is mounted on a PATH, and Google
      matches redirect URIs exactly, so the prefix has to be there.
   2. The sessionStorage keys are `rof_*`. They were `rev_*` in the copy source; sharing keys
      across CORE apps on different origins is harmless, but this app shares an ORIGIN with
      anything else that ever lands on the apex, and two half-finished logins clobbering each
      other's PKCE verifier is a genuinely confusing failure. */

import { pb } from './pb';

const SS = {
  verifier: 'rof_g_verifier',
  state: 'rof_g_state',
  redirect: 'rof_g_redirect',
  returnTo: 'rof_g_return_to',
} as const;

type Provider = { name?: string; authURL?: string; codeVerifier?: string; state?: string };

/** Must exactly match an Authorized redirect URI on the Google OAuth client. */
export function googleCallbackRedirectURL(): string {
  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  // import.meta.env.BASE_URL is '/real-or-fake/' — trailing slash included.
  return `${origin}${import.meta.env.BASE_URL}auth/google/callback`;
}

export function rememberReturnPath(path: string): void {
  try {
    if (path && !path.startsWith('/auth/google/callback')) sessionStorage.setItem(SS.returnTo, path);
  } catch {
    /* sessionStorage unavailable — we just land on the default route */
  }
}

export function takeReturnPath(): string {
  try {
    const p = sessionStorage.getItem(SS.returnTo);
    sessionStorage.removeItem(SS.returnTo);
    return p || '/';
  } catch {
    return '/';
  }
}

/** Step 1 — send the browser to Google. */
export async function startGoogleLogin(): Promise<{ ok: boolean; error?: string; redirecting?: boolean }> {
  let methods: unknown;
  try {
    methods = await pb.collection('users').listAuthMethods();
  } catch {
    return { ok: false, error: 'Could not reach the sign-in service. Check your connection and try again.' };
  }

  const m = methods as { oauth2?: { providers?: Provider[] }; authProviders?: Provider[] };
  const providers = m?.oauth2?.providers || m?.authProviders || [];
  const provider = providers.find((p) => String(p?.name || '').toLowerCase() === 'google');
  if (!provider?.authURL) return { ok: false, error: 'Google sign-in is not available right now.' };

  const redirectURL = googleCallbackRedirectURL();
  try {
    sessionStorage.setItem(SS.verifier, provider.codeVerifier || '');
    sessionStorage.setItem(SS.state, provider.state || '');
    sessionStorage.setItem(SS.redirect, redirectURL);
  } catch {
    /* the callback will fail cleanly with "session expired" rather than silently */
  }

  // provider.authURL ends with `redirect_uri=` — append ours, URL-encoded.
  window.location.href = provider.authURL + encodeURIComponent(redirectURL);
  return { ok: true, redirecting: true };
}

/** Step 2 — runs on the callback route; exchanges the code for a real PB session. */
export async function completeGoogleLogin(search?: string): Promise<{ ok: boolean; error?: string }> {
  const params = new URLSearchParams(
    typeof search === 'string' ? search : typeof window !== 'undefined' ? window.location.search : '',
  );
  if (params.get('error')) return { ok: false, error: 'Google sign-in was cancelled.' };

  const code = params.get('code');
  const returnedState = params.get('state');

  let verifier = '';
  let savedState = '';
  let redirectURL = '';
  try {
    verifier = sessionStorage.getItem(SS.verifier) || '';
    savedState = sessionStorage.getItem(SS.state) || '';
    redirectURL = sessionStorage.getItem(SS.redirect) || '';
  } catch {
    /* handled below */
  }
  if (!redirectURL) redirectURL = googleCallbackRedirectURL();

  if (!code || !verifier) return { ok: false, error: 'Your sign-in session expired. Please try again.' };
  if (savedState && returnedState && savedState !== returnedState) {
    return { ok: false, error: 'Sign-in verification failed. Please try again.' };
  }

  try {
    await pb.collection('users').authWithOAuth2Code('google', code, verifier, redirectURL);
  } catch {
    return { ok: false, error: 'Google sign-in failed. Please try again.' };
  } finally {
    try {
      sessionStorage.removeItem(SS.verifier);
      sessionStorage.removeItem(SS.state);
      sessionStorage.removeItem(SS.redirect);
    } catch {
      /* ignore */
    }
  }
  return { ok: true };
}
